import {
  ActionIcon,
  Button,
  Flex,
  Group,
  Menu,
  SegmentedControl,
  Tooltip,
  type SegmentedControlItem
} from "@mantine/core";
import {
  IconDotsVertical,
  IconFileTypeXls,
  IconFileX,
  IconMail,
  IconPlus
} from "@tabler/icons-react";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef
} from "mantine-react-table-open";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePersons } from "../../hooks/usePersons";
import { useSapExport } from "../../hooks/useSapExport";
import { useSites } from "../../hooks/useSites";
import { getDefaultMRTOptions } from "../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { useToast } from "../../mantine/notifications/useToast";
import { useResetPageOnFilterOrSortChange } from "../../mantine/useResetPageOnFilterOrSortChange";
import type {
  InvoiceState,
  InvoiceData,
  InvoiceResponse,
  Person
} from "../../utils/backend-types";
import { THEME_VARS } from "../../utils/constants";
import { BasicConfirmationModal } from "../BuildingBlocks/Layout/Modals/BasicConfirmationModal/BasicConfirmationModal";
import {
  type Contract,
  type Doc
} from "../DeliveryOverview/DeliveryOverview.types";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { getBillingPeriodColumn } from "./columns/getBillingPeriodColumn";
import { getCreatedColumn } from "./columns/getCreatedColumn";
import { getGenerateInvoiceColumn } from "./columns/getGenerateInvoiceColumn";
import { getInvoiceDocumentColumn } from "./columns/getInvoiceDocumentColumn";
import { getSiteColumn } from "./columns/getSiteColumn";
import { getStateColumn } from "./columns/getStateColumn";
import { getSuppliedColumn } from "./columns/getSuppliedColumn";
import { getSupplierColumn } from "./columns/getSupplierColumn";
import { getTypeColumn } from "./columns/getTypeColumn";
import "./DeliveryInvoices.scss";
import { useInvoices } from "./hooks/useInvoices";
import { useInvoiceMutations } from "./hooks/useInvoicesMutations";
import { usePendingInvoices } from "./hooks/usePendingInvoices";
import { usePreviewInvoices } from "./hooks/usePreviewInvoices";
import { SapExportModal } from "./SapExportModal/SapExportModal";
import { buildDraftInvoices } from "./utils/buildDraftInvoices";

const documentTypeFilterOptions: Array<SegmentedControlItem> = [
  { value: "all", label: "Alle" },
  { value: "draft", label: "Vorschau" },
  { value: "invoice", label: "Rechnung" },
  {
    value: "reversed",
    label: "Stornierte Rechnung"
  },
  { value: "reversal", label: "Stornobeleg" }
] as const;

// will be removed with redesign go-live
export type Invoice = Omit<InvoiceResponse, "invoiceData"> &
  Partial<Omit<InvoiceData, "id">> & {
    supplied?: number;
    supplier?: number;
    siteName?: string;
  };

interface DeliveryInvoicesProps {
  projectId: string;
  variantId: number;
  contracts: Array<Contract>;
}

function DeliveryInvoices({
  projectId,
  variantId,
  contracts
}: DeliveryInvoicesProps) {
  const { showToast, showErrorToast } = useToast();
  const { t } = useTranslation();
  const [invoicesToReverse, setInvoicesToReverse] = useState<Array<Doc["id"]>>(
    []
  );
  const [invoicesToSend, setInvoicesToSend] = useState<Array<Doc["id"]>>([]);
  const [sapExportModalOpen, setSapExportModalOpen] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    document: Doc;
    contract: Contract;
    person: Person;
  } | null>(null);

  const { generateAndDownloadSapExport, exportLoading } = useSapExport();
  const {
    data: sites,
    isLoading: isSitesLoading,
    error: sitesError
  } = useSites({ projectId });
  const {
    isLoading: isPersonsLoading,
    error: personsError,
    persons
  } = usePersons({ variantId });
  const {
    data: invoices,
    isLoading: isInvoicesLoading,
    error: invoicesError
  } = useInvoices(projectId);
  const {
    data: pendingInvoices,
    isLoading: isPendingInvoicesLoading,
    error: pendingInvoicesError
  } = usePendingInvoices(projectId);
  const { generateAndDownloadPreview, loadingPreviews } = usePreviewInvoices();
  const {
    generateInvoices,
    generateSingleInvoice,
    reverseInvoice,
    sendMail,
    setMailAllowance,
    updateInvoiceState
  } = useInvoiceMutations(projectId);

  const isGeneratingInvoices =
    generateInvoices.isPending || generateSingleInvoice.isPending;

  function handleReverseInvoices(ids: Array<string>) {
    setInvoicesToReverse(ids);

    const allMutations = ids.map((id) => reverseInvoice.mutateAsync(id));
    Promise.all(allMutations).finally(() => {
      setInvoicesToReverse([]);
    });
  }

  async function handleSendMail(document: Doc) {
    setInvoicesToSend([document.id]);
    const person = persons.find((p) => p.id === document.supplied);
    const contract = contracts.find(
      (c) => c.id === document.invoiceData?.contract
    );

    if (person && contract) {
      if (contract.allowance_send_invoice_via_mail) {
        try {
          await sendMail.mutateAsync(document.id);
          showToast({
            type: "success",
            message: "Die E-Mail wurde erfolgreich verschickt."
          });
        } catch (error) {
          showErrorToast(error);
        }
        setInvoicesToSend([]);
      } else {
        setConfirmationData({
          document,
          contract: contract,
          person: person
        });
      }
    }
  }

  async function handleSendMailConfimed() {
    if (confirmationData) {
      await setMailAllowance
        .mutateAsync(confirmationData.contract)
        .then(() => {
          sendMail
            .mutateAsync(confirmationData.document.id)
            .then(() => {
              showToast({
                type: "success",
                message: "Die E-Mail wurde erfolgreich verschickt."
              });
            })
            .catch((error) => {
              showErrorToast(error);
            });
        })
        .catch(() => {
          showErrorToast(t("errors.DeliveryInvoices.AllowSendError"));

          setInvoicesToSend([]);
          setConfirmationData(null);
        });
    }
  }

  async function handleGenerateInvoices() {
    try {
      await generateInvoices.mutateAsync();
    } catch (error) {
      showErrorToast(error);
      return;
    }

    showToast({
      type: "success",
      message: "Alle angeforderten Rechnungen wurden erstellt."
    });
  }

  const handleGenerateSingleInvoice = useCallback(
    async (document: Doc) => {
      if (!document.pendingInvoice) {
        showErrorToast(t("errors.DeliveryInvoices.GenerateInvoiceError"));
        return;
      }

      try {
        await generateSingleInvoice.mutateAsync(document.pendingInvoice);

        showToast({
          type: "success",
          message: "Die Rechnung wurde erfolgreich erstellt."
        });
      } catch (error) {
        showErrorToast(error);
      }
    },
    [generateSingleInvoice, showErrorToast, showToast, t]
  );

  const suppliers = useMemo(() => {
    const uniqueSupplierIds = [...new Set(contracts.map((c) => c.supplier))];
    return uniqueSupplierIds
      .map((id) => {
        const person = persons.find((p) => p.id === id);
        return person
          ? { value: person.id.toString(), label: person.name }
          : null;
      })
      .filter((s) => s !== null);
  }, [contracts, persons]);

  function handleClickSapExport(
    dateFrom: string,
    dateTo: string,
    supplierId: string | null
  ) {
    generateAndDownloadSapExport(
      projectId,
      dateFrom,
      dateTo,
      supplierId ?? undefined
    ).finally(() => {
      setSapExportModalOpen(false);
    });
  }

  const columns = useMemo<Array<MRT_ColumnDef<Doc>>>(() => {
    async function handleChangeInvoiceStatus(
      invoiceId: string,
      state: InvoiceState
    ) {
      try {
        await updateInvoiceState.mutateAsync({ id: invoiceId, state });
        showToast({
          type: "success",
          message: "Der Status der Rechnung wurde erfolgreich aktualisiert."
        });
      } catch (error) {
        showErrorToast(error);
      }
    }

    return [
      getBillingPeriodColumn(),
      getInvoiceDocumentColumn(persons),
      getTypeColumn(generateAndDownloadPreview, loadingPreviews),
      getCreatedColumn(),
      getSiteColumn(sites || []),
      getSupplierColumn(persons),
      getSuppliedColumn(persons),
      getStateColumn(handleChangeInvoiceStatus),
      getGenerateInvoiceColumn(
        !isGeneratingInvoices,
        handleGenerateSingleInvoice
      )
    ];
  }, [
    isGeneratingInvoices,
    persons,
    sites,
    loadingPreviews,
    updateInvoiceState,
    generateAndDownloadPreview,
    handleGenerateSingleInvoice,
    showToast,
    showErrorToast
  ]);

  const tableData: Array<Doc> = useMemo(() => {
    const drafts = buildDraftInvoices(pendingInvoices ?? [], projectId);

    if (!invoices) {
      return drafts;
    } else {
      const plainInvoices: Array<Doc> = invoices
        .filter((invoice) => invoice.reversalInvoice === null)
        .map((invoice) => {
          const contract = contracts.find(
            (contract) => contract.id === invoice.invoiceData?.contract
          );
          return {
            ...invoice,
            documentType: "invoice",
            supplier: contract?.supplier,
            supplied: contract?.supplied,
            siteName:
              sites?.find((site) => site.id === invoice.invoiceData?.site)
                ?.name || ""
          };
        });

      const reversed: Array<InvoiceResponse> = invoices.filter(
        (invoice) => invoice.reversalInvoice !== null
      );
      const reversedInvoices: Array<Doc> = reversed.map((invoice) => {
        const contract = contracts.find(
          (contract) => contract.id === invoice.invoiceData?.contract
        );
        return {
          ...invoice,
          documentType: "reversed",
          supplier: contract?.supplier,
          supplied: contract?.supplied,
          siteName:
            sites?.find((site) => site.id === invoice.invoiceData?.site)
              ?.name || ""
        };
      });
      const reversals: Array<Doc> = reversed.map((invoice) => {
        const contract = contracts.find(
          (contract) => contract.id === invoice.invoiceData?.contract
        );
        return {
          ...invoice,
          ...invoice.reversalInvoice,
          reversesInvoice: invoice.id,
          documentType: "reversal",
          supplier: contract?.supplier,
          supplied: contract?.supplied,
          siteName:
            sites?.find((site) => site.id === invoice.invoiceData?.site)
              ?.name || ""
        };
      });

      return [...drafts, ...plainInvoices, ...reversedInvoices, ...reversals];
    }
  }, [pendingInvoices, projectId, invoices, contracts, sites]);

  const resetPageState = useResetPageOnFilterOrSortChange({
    initialColumnFilters: [{ id: "documentType", value: "all" }],
    initialSorting: [{ id: "billingPeriodStart", desc: true }]
  });

  const mrtTable = useMantineReactTable<Doc>({
    ...getDefaultMRTOptions({
      initialState: {
        showGlobalFilter: true
      },
      hideBorder: true
    }),
    onGlobalFilterChange: resetPageState.onGlobalFilterChange,
    onColumnFiltersChange: resetPageState.onColumnFiltersChange,
    onSortingChange: resetPageState.onSortingChange,
    onPaginationChange: resetPageState.onPaginationChange,
    state: {
      ...resetPageState.state,
      // invoicesToReverse.length > 0 because when reversing many invoices, isInvoicesFetching is true - false - true - false - ...
      isLoading: invoicesToReverse.length > 0 || generateInvoices.isPending
    },

    ...getDefaultMRTRowEditModeOptions(),
    // need to overwrite the default row edit mode options
    renderRowActions: ({ row }) => {
      const canReverse = row.original.documentType === "invoice";
      return (
        <Flex gap="sm" justify="flex-end">
          <Tooltip label="Per E-Mail versenden">
            <ActionIcon
              aria-label="Per E-Mail versenden"
              color="black"
              disabled={
                row.original.documentType === "draft" ||
                row.original.documentType === "reversed"
              }
              loading={invoicesToSend.includes(row.original.id)}
              size="1.2rem"
              variant="transparent"
              onClick={() => handleSendMail(row.original)}
            >
              <IconMail />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Stornieren">
            <ActionIcon
              aria-label="Stornieren"
              color="error"
              disabled={!canReverse}
              loading={invoicesToReverse.includes(row.original.id)}
              size="1.2rem"
              variant="transparent"
              onClick={() => handleReverseInvoices([row.original.id])}
            >
              <IconFileX />
            </ActionIcon>
          </Tooltip>
        </Flex>
      );
    },

    mantineTopToolbarProps: () => ({ m: 0 }),
    ...getMRTTopToolbarCustomActionsRenderer(),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <Group justify="flex-end" ms="xl" w="stretch">
          <SegmentedControl
            color="primary"
            data={documentTypeFilterOptions}
            defaultValue="all"
            onChange={(newDocType) => {
              table.setColumnFilters((prev) => [
                ...prev,
                { id: "documentType", value: newDocType }
              ]);
            }}
          />
          <Menu>
            <Menu.Target>
              <Button
                aria-label="Aktionen"
                color="black"
                leftSection={<IconDotsVertical size="1.2rem" />}
                variant="transparent"
              >
                Weitere Optionen
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                disabled={isGeneratingInvoices}
                leftSection={<IconPlus size="1.2rem" />}
                onClick={handleGenerateInvoices}
              >
                Alle Rechnungen erstellen
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFileTypeXls size="1.2rem" />}
                onClick={() => setSapExportModalOpen(true)}
              >
                Alle Rechnungen exportieren
              </Menu.Item>
              <Menu.Item
                disabled={table.getSelectedRowModel().flatRows.length === 0}
                leftSection={
                  <IconFileX color={THEME_VARS.error[6]} size="1.2rem" />
                }
                onClick={() =>
                  handleReverseInvoices(
                    table
                      .getSelectedRowModel()
                      .flatRows.map((row) => row.original.id)
                  )
                }
              >
                Auswahl stornieren
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      );
    },

    enableColumnResizing: true,
    autoResetPageIndex: false,
    enableColumnFilters: true,
    columnFilterDisplayMode: "popover",
    enableFacetedValues: true,
    columns: columns,
    data: tableData
  });

  return (
    <LoadOrError
      error={
        sitesError || personsError || invoicesError || pendingInvoicesError
      }
      loading={
        isSitesLoading ||
        isPersonsLoading ||
        isInvoicesLoading ||
        isPendingInvoicesLoading
      }
    >
      <MantineReactTable table={mrtTable} />

      <BasicConfirmationModal
        opened={confirmationData !== null}
        toggleModal={() => {
          setConfirmationData(null);
          setInvoicesToSend([]);
        }}
        onCancel={() => {
          setConfirmationData(null);
          setInvoicesToSend([]);
        }}
        onConfirm={handleSendMailConfimed}
      >
        {confirmationData !== null &&
          `Liegt eine Einwilligung von ${confirmationData.person.name} für den
        elektronischen Rechnungsversand vor?`}
      </BasicConfirmationModal>
      <SapExportModal
        isLoading={exportLoading}
        isModalOpen={sapExportModalOpen}
        suppliers={suppliers}
        onClickExport={handleClickSapExport}
        onCloseModal={() => setSapExportModalOpen(false)}
      />
    </LoadOrError>
  );
}

export { DeliveryInvoices, DeliveryInvoicesProps };
